import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import "../AdminDashboard/AdminDashboard.css";
import "./GymAssets.css";
import { formatDate, hoursLabel, jobTypeLabel, money, statusLabel } from "./gymAssetOptions";

export default function FacilityDashboard() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (branch_id = branchId) => {
    setLoading(true);
    try {
      const res = await UserServices.gymFacilityDashboard({ branch_id: branch_id || undefined });
      setData(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load the facility dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    UserServices.branchNameAll().then((res) => setBranches(res.data?.data || [])).catch(() => setBranches([]));
    load("");
  }, []);

  const summary = data?.summary || {};

  return (
    <div className="wrapper gym-assets">
      <ToastContainer />
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar d-flex justify-content-between align-items-start gap-3">
              <div>
                <p className="ad-eyebrow">Facility</p>
                <h2 className="ad-title">Facility dashboard</h2>
                <p className="ad-subtitle">Assets, open repairs, maintenance cost, downtime, and warranties that are about to end.</p>
              </div>
              <div className="gym-actions">
                <select className="form-select" value={branchId} onChange={(event) => { setBranchId(event.target.value); load(event.target.value); }}>
                  <option value="">All branches</option>
                  {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                </select>
                <Link className="btn btn-outline-secondary" to="/gym-assets">Assets</Link>
                <Link className="btn btn-danger" to="/gym-maintenance">Maintenance</Link>
              </div>
            </div>

            <div className="gym-kpis mb-3">
              <div className="gym-kpi"><span>Units</span><strong>{loading ? "—" : summary.units ?? 0}</strong></div>
              <div className="gym-kpi"><span>Purchase cost</span><strong>{loading ? "—" : money(summary.purchase_cost)}</strong></div>
              <div className="gym-kpi"><span>Working</span><strong>{loading ? "—" : summary.working ?? 0}</strong></div>
              <div className="gym-kpi is-alert"><span>Under repair</span><strong>{loading ? "—" : summary.under_repair ?? 0}</strong></div>
              <div className="gym-kpi"><span>Repair cost</span><strong>{loading ? "—" : money(summary.maintenance_cost)}</strong></div>
              <div className="gym-kpi"><span>Downtime</span><strong>{loading ? "—" : hoursLabel(summary.downtime_minutes)}</strong></div>
            </div>

            <div className="gym-split mb-3">
              <section className="gym-panel p-3">
                <div className="gym-panel-head">
                  <h3>Under repair</h3>
                  <Link to="/gym-assets">Open register</Link>
                </div>
                {(data?.under_repair || []).length === 0 ? <p className="gym-muted mb-0">Nothing is under repair.</p> : (
                  <div className="gym-link-list">
                    {data.under_repair.map((row) => (
                      <Link key={row.id} to={`/gym-assets/${row.id}`}>
                        {row.name}
                        <div className="gym-asset-meta">{row.serial_number || "No serial"} · {row.asset_code} · {row.branch_name}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
              <section className="gym-panel p-3">
                <div className="gym-panel-head">
                  <h3>Recent maintenance</h3>
                  <Link to="/gym-maintenance">Open maintenance</Link>
                </div>
                {(data?.recent_jobs || []).length === 0 ? <p className="gym-muted mb-0">No maintenance recorded yet.</p> : (
                  <table className="table gym-table mb-0">
                    <thead><tr><th>Asset</th><th>Work</th><th>Cost</th></tr></thead>
                    <tbody>
                      {data.recent_jobs.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <Link className="gym-asset-name" to={`/gym-maintenance?asset=${row.asset_id}`}>{row.asset_name}</Link>
                            <div className="gym-asset-meta">{row.serial_number || row.asset_code}</div>
                          </td>
                          <td>
                            <div>{row.title}</div>
                            <div className="gym-asset-meta">{jobTypeLabel(row.job_type)} · {formatDate(row.started_at)} · {row.status === "open" ? "Open" : "Closed"}</div>
                          </td>
                          <td>{money(row.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </div>

            <div className="gym-split">
              <section className="gym-panel p-3">
                <div className="gym-panel-head"><h3>By category</h3></div>
                {(summary.by_category || []).length === 0 ? <p className="gym-muted mb-0">No assets yet.</p> : (
                  <table className="table gym-table mb-0">
                    <thead><tr><th>Category</th><th>Units</th><th>Purchase cost</th></tr></thead>
                    <tbody>
                      {summary.by_category.map((row) => (
                        <tr key={row.category}>
                          <td>{row.category}</td>
                          <td>{row.units}</td>
                          <td>{money(row.purchase_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
              <section className="gym-panel p-3">
                <div className="gym-panel-head">
                  <h3>Warranty ending in 30 days</h3>
                  <span className="gym-count">{summary.warranty_due || 0}</span>
                </div>
                {(data?.warranty_due || []).length === 0 ? <p className="gym-muted mb-0">No warranties are due.</p> : (
                  <div className="gym-link-list">
                    {data.warranty_due.map((row) => (
                      <Link key={row.id} to={`/gym-assets/${row.id}`}>
                        {row.name}
                        <div className="gym-asset-meta">{row.serial_number || row.asset_code} · ends {formatDate(row.warranty_end)} · {statusLabel(row.status)}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
